import { createStore, createEvent, createEffect, sample } from "effector";
import { TreeCheckboxSelectionKeys } from "primereact/tree";
import {
  AccountSetupState,
  AccountSetupFormData,
  OrganizationDetails,
  OrganizationSearchResult,
  OrganizationSearchResponse,
  ProposedPartnersResponse,
  PlansResponse,
  UpdateProfilePayload,
  CreateOrganizationPayload,
  Plan,
} from "../model/types";
import { accountSetupService } from "../service/api";

// ============================================
// Events
// ============================================

// Navigation events
export const setCurrentStep = createEvent<number>();
export const goToNextStep = createEvent();
export const goToPreviousStep = createEvent();

// Form data events
export const updateFormData = createEvent<Partial<AccountSetupFormData>>();
export const setJobTitle = createEvent<string>();
export const setOrganizationDetails = createEvent<OrganizationDetails>();
export const setOrganizationDescription = createEvent<string>();

// Selection events
export const setSelectedNodes = createEvent<TreeCheckboxSelectionKeys | null>();
export const setSelectedExpertise = createEvent<string[]>();
export const toggleExpertise = createEvent<string>();
export const setSelectedOrgSectors =
  createEvent<TreeCheckboxSelectionKeys | null>();
export const setOrgDescription = createEvent<string>();

// Organization search events
export const setOrganizationSearchQuery = createEvent<string>();
export const searchOrganizationsRequested = createEvent<string>();
export const clearOrganizationSearchResults = createEvent();
export const selectOrganization = createEvent<OrganizationSearchResult>();
export const removeOrganization = createEvent<string>();

// Proposed partners events
export const fetchProposedPartnersRequested = createEvent();

// Plans events
export const fetchPlansRequested = createEvent();
export const setSelectedPlan = createEvent<Plan | null>();

// Completion events
export const completeAccountSetupRequested = createEvent<{
  formData: Partial<AccountSetupFormData>;
  selectedOrganizations: OrganizationSearchResult[];
  expertiseTerms: Array<{ tid: string; name: string }>;
  countriesTerms: Array<{ tid: string; name: string }>;
  sectorsTerms: Array<{ tid: string; name: string }>;
  fundingTypeTerms: Array<{ tid: string; name: string }>;
}>();

// UI events
export const toggleSectorExpansion = createEvent<string>();
export const resetAccountSetup = createEvent();

// ============================================
// Effects
// ============================================

export const searchOrganizationsFx = createEffect(
  async (query: string): Promise<OrganizationSearchResponse> => {
    if (query.length < 3) {
      return { data: [] };
    }
    return await accountSetupService.searchOrganizations(query);
  },
);

export const fetchProposedPartnersFx = createEffect(
  async (): Promise<ProposedPartnersResponse> => {
    return await accountSetupService.getProposedPartners();
  },
);

export const fetchPlansFx = createEffect(async (): Promise<PlansResponse> => {
  return await accountSetupService.getPlans();
});

export const updateProfileFx = createEffect(
  async (payload: UpdateProfilePayload): Promise<void> => {
    await accountSetupService.updateProfile(payload);
  },
);

export const createOrganizationFx = createEffect(
  async (payload: CreateOrganizationPayload): Promise<void> => {
    await accountSetupService.createOrganization(payload);
  },
);

export const askToJoinOrganizationFx = createEffect(
  async (orgId: string): Promise<void> => {
    await accountSetupService.askToJoinOrganization(orgId);
  },
);

export const completeAccountSetupFx = createEffect(
  async (params: {
    formData: Partial<AccountSetupFormData>;
    selectedOrganizations: OrganizationSearchResult[];
    expertiseTerms: Array<{ tid: string; name: string }>;
    countriesTerms: Array<{ tid: string; name: string }>;
    sectorsTerms: Array<{ tid: string; name: string }>;
    fundingTypeTerms: Array<{ tid: string; name: string }>;
    submitPartnerOnly?: boolean;
  }): Promise<void> => {
    const {
      formData,
      selectedOrganizations,
      expertiseTerms,
      countriesTerms,
      sectorsTerms,
      fundingTypeTerms,
      submitPartnerOnly,
    } = params;

    // Conditionally update user profile
    if (!submitPartnerOnly) {
      const profilePayload: UpdateProfilePayload = {
        field_job_title: formData.jobTitle || "",
        field_sectors:
          formData.interests?.map((interest) => interest.tid) || [],
        field_expertise:
          formData.euExpertise
            ?.map((name) => expertiseTerms.find((t) => t.name === name)?.tid)
            .filter((tid): tid is string => !!tid) || [],
      };

      await accountSetupService.updateProfile(profilePayload);
    }

    // Handle join organization
    if (
      formData.organizationAction === "join" &&
      selectedOrganizations.length > 0
    ) {
      for (const org of selectedOrganizations) {
        await accountSetupService.askToJoinOrganization(org.id);
      }
    }

    // Handle create organization
    if (
      formData.organizationAction === "create" &&
      formData.organizationDetails
    ) {
      const orgPayload: CreateOrganizationPayload = {
        field_pic_number: formData.organizationDetails.field_pic_number || "",
        title: formData.organizationDetails.title,
        field_legal_form: [
          formData.organizationDetails.field_legal_form
            ? formData.organizationDetails.field_legal_form
            : "",
        ],
        field_country: [
          countriesTerms.find(
            (t) => t.name === formData.organizationDetails?.field_country,
          )?.tid || "0",
        ],
        field_year_of_establishment:
          formData.organizationDetails.field_year_of_establishment || "",
        field_city: formData.organizationDetails.field_city || "",
        field_phone_number: formData.organizationDetails.phone_number || "",
        field_expertise: formData.organizationDetails.field_expertise
          ? "1"
          : "0",
        field_type:
          formData.organizationDetails.field_type
            ?.map((name) => fundingTypeTerms.find((t) => t.name === name)?.tid)
            .filter((tid): tid is string => !!tid) || [],
        field_sectors:
          formData.organizationSectors
            ?.map((name) => sectorsTerms.find((t) => t.name === name)?.tid)
            .filter((tid): tid is string => !!tid) || [],
        field_description: formData.organizationDescription || "",
      };

      await accountSetupService.createOrganization(orgPayload);
    }
  },
);

// ============================================
// Store
// ============================================

const initialState: AccountSetupState = {
  currentStep: 2,
  formData: {},
  selectedNodes: null,
  selectedExpertise: [],
  selectedOrgSectors: null,
  orgDescription: "",
  organizationSearchQuery: "",
  organizationSearchResults: [],
  organizationSearchLoading: false,
  selectedOrganizations: [],
  proposedPartners: [],
  proposedPartnersLoading: false,
  plans: [],
  plansLoading: false,
  selectedPlan: null,
  isCompleting: false,
  completionError: null,
  expandedSectors: new Set(),
};

export const $accountSetupStore = createStore<AccountSetupState>(initialState)
  // Navigation
  .on(setCurrentStep, (state, step) => ({ ...state, currentStep: step }))
  .on(goToNextStep, (state) => ({
    ...state,
    currentStep: state.currentStep + 1,
  }))
  .on(goToPreviousStep, (state) => ({
    ...state,
    currentStep: Math.max(2, state.currentStep - 1),
  }))

  // Form data
  .on(updateFormData, (state, data) => ({
    ...state,
    formData: { ...state.formData, ...data },
  }))
  .on(setJobTitle, (state, jobTitle) => ({
    ...state,
    formData: { ...state.formData, jobTitle },
  }))
  .on(setOrganizationDetails, (state, organizationDetails) => ({
    ...state,
    formData: { ...state.formData, organizationDetails },
  }))
  .on(setOrganizationDescription, (state, organizationDescription) => ({
    ...state,
    formData: { ...state.formData, organizationDescription },
  }))

  // Selections
  .on(setSelectedNodes, (state, selectedNodes) => ({ ...state, selectedNodes }))
  .on(setSelectedExpertise, (state, selectedExpertise) => ({
    ...state,
    selectedExpertise,
  }))
  .on(toggleExpertise, (state, expertise) => ({
    ...state,
    selectedExpertise: state.selectedExpertise.includes(expertise)
      ? state.selectedExpertise.filter((e) => e !== expertise)
      : [...state.selectedExpertise, expertise],
  }))
  .on(setSelectedOrgSectors, (state, selectedOrgSectors) => ({
    ...state,
    selectedOrgSectors,
  }))
  .on(setOrgDescription, (state, orgDescription) => ({
    ...state,
    orgDescription,
  }))

  // Organization search
  .on(setOrganizationSearchQuery, (state, query) => ({
    ...state,
    organizationSearchQuery: query,
  }))
  .on(searchOrganizationsRequested, (state) => ({
    ...state,
    organizationSearchLoading: true,
  }))
  .on(searchOrganizationsFx.doneData, (state, response) => ({
    ...state,
    organizationSearchResults: response.data,
    organizationSearchLoading: false,
  }))
  .on(searchOrganizationsFx.failData, (state) => ({
    ...state,
    organizationSearchResults: [],
    organizationSearchLoading: false,
  }))
  .on(clearOrganizationSearchResults, (state) => ({
    ...state,
    organizationSearchResults: [],
  }))
  .on(selectOrganization, (state, org) => {
    const isAlreadySelected = state.selectedOrganizations.some(
      (s) => s.id === org.id,
    );
    if (isAlreadySelected) return state;
    return {
      ...state,
      selectedOrganizations: [...state.selectedOrganizations, org],
    };
  })
  .on(removeOrganization, (state, orgId) => ({
    ...state,
    selectedOrganizations: state.selectedOrganizations.filter(
      (org) => org.id !== orgId,
    ),
  }))

  // Proposed partners
  .on(fetchProposedPartnersRequested, (state) => ({
    ...state,
    proposedPartnersLoading: true,
  }))
  .on(fetchProposedPartnersFx.doneData, (state, response) => ({
    ...state,
    proposedPartners: response.data,
    proposedPartnersLoading: false,
  }))
  .on(fetchProposedPartnersFx.failData, (state) => ({
    ...state,
    proposedPartners: [],
    proposedPartnersLoading: false,
  }))

  // Plans
  .on(fetchPlansRequested, (state) => ({ ...state, plansLoading: true }))
  .on(fetchPlansFx.doneData, (state, response) => ({
    ...state,
    plans: response.data,
    plansLoading: false,
  }))
  .on(fetchPlansFx.failData, (state) => ({
    ...state,
    plans: [],
    plansLoading: false,
  }))
  .on(setSelectedPlan, (state, plan) => ({ ...state, selectedPlan: plan }))

  // Completion
  .on(completeAccountSetupRequested, (state) => ({
    ...state,
    isCompleting: true,
    completionError: null,
  }))
  .on(completeAccountSetupFx.doneData, (state) => ({
    ...state,
    isCompleting: false,
    completionError: null,
  }))
  .on(completeAccountSetupFx.failData, (state, error) => ({
    ...state,
    isCompleting: false,
    completionError:
      error.message || "Setup completion failed. Please try again.",
  }))

  // UI
  .on(toggleSectorExpansion, (state, partnerId) => {
    const newSet = new Set(state.expandedSectors);
    if (newSet.has(partnerId)) {
      newSet.delete(partnerId);
    } else {
      newSet.add(partnerId);
    }
    return { ...state, expandedSectors: newSet };
  })
  .on(resetAccountSetup, () => initialState);

// ============================================
// Samples
// ============================================

sample({
  clock: searchOrganizationsRequested,
  target: searchOrganizationsFx,
});

sample({
  clock: fetchProposedPartnersRequested,
  target: fetchProposedPartnersFx,
});

sample({
  clock: fetchPlansRequested,
  target: fetchPlansFx,
});

sample({
  clock: completeAccountSetupRequested,
  target: completeAccountSetupFx,
});
